const REASON_MAP = {
    Tip: 'wallet_reason_tip',
    'Cancellation Fee': 'wallet_reason_cancellation_fee',
    'Admin Credit': 'wallet_reason_admin_credit',
    AdminCredit: 'wallet_reason_admin_credit',
    'Referral Credit': 'wallet_reason_referral',
    'Cancellation penalty': 'wallet_reason_penalty',
    AdRecharge: 'wallet_reason_ad_recharge',
    AdClick: 'wallet_reason_ad_click',
    Withdraw: 'wallet_reason_withdraw',
    WithdrawRequest: 'wallet_reason_withdraw',
    WithdrawRejected: 'wallet_reason_withdraw_rejected',
    auto_recharge: 'wallet_reason_auto_recharge',
    daviplata_wallet_recharge: 'wallet_reason_wallet_recharge',
    'Comisión plataforma (efectivo)': 'wallet_reason_platform_fee_cash',
    'Ganancia por servicio': 'wallet_reason_trip_earning',
    'Pago de servicio': 'wallet_reason_trip_payment',
};

const DEFAULT_REASONS = {
    wallet_reason_tip: 'Propina',
    wallet_reason_cancellation_fee: 'Penalización por cancelación',
    wallet_reason_admin_credit: 'Abono administrativo',
    wallet_reason_referral: 'Crédito por referido',
    wallet_reason_penalty: 'Penalización por cancelación',
    wallet_reason_ad_recharge: 'Impulso de perfil',
    wallet_reason_ad_click: 'Cargo por impulso (servicio)',
    wallet_reason_withdraw: 'Retiro de billetera',
    wallet_reason_withdraw_rejected: 'Retiro rechazado (reintegro)',
    wallet_reason_auto_recharge: 'Recarga automática',
    wallet_reason_platform_fee_cash: 'Comisión de plataforma (efectivo)',
    wallet_reason_trip_payment: 'Pago de servicio',
    wallet_reason_trip_earning: 'Ingreso por servicio',
    wallet_reason_wallet_recharge: 'Recarga de billetera',
    wallet_reason_operation: 'Operación de billetera',
    wallet_reason_other: 'Movimiento de billetera',
};

const applyTemplate = (template, vars) =>
    Object.keys(vars).reduce(
        (msg, key) => msg.replace(new RegExp(`\\{${key}\\}`, 'g'), vars[key] || ''),
        template
    );

const normalizeTxType = (type) => {
    const t = (type || '').toString().toLowerCase();
    if (t.includes('credit')) return 'Credit';
    if (t.includes('withdraw')) return 'Withdraw';
    if (t.includes('debit')) return 'Debit';
    return type;
};

const translateReasonKey = (key, lang) => {
    if (key && lang[key]) return lang[key];
    if (key && DEFAULT_REASONS[key]) return DEFAULT_REASONS[key];
    return null;
};

const isKnownReasonCode = (value) => {
    const s = String(value || '').trim();
    return !!s && !!REASON_MAP[s];
};

/** Referencias internas (Firebase, reservas) que no deben mostrarse como "concepto". */
const isOpaqueReference = (value) => {
    const s = String(value || '').trim();
    if (!s) return false;
    if (isKnownReasonCode(s)) return false;
    if (/^wallet-/i.test(s)) return true;
    if (/^penalty_/i.test(s)) return true;
    if (/^T_[A-Za-z0-9]+$/.test(s)) return true;
    if (/^-?[A-Za-z0-9_-]{10,}$/.test(s)) return true;
    return false;
};

export const formatAmount = (amount, settings) => {
    const decimal = settings && typeof settings.decimal === 'number' ? settings.decimal : 2;
    const symbol = (settings && settings.symbol) || '$';
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return `${symbol}0`;
    const formatted = n.toLocaleString('es-CO', {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
    });
    return `${symbol}${formatted}`;
};

export const resolveReason = (description, language, note) => {
    const lang = language || {};
    if (note && String(note).trim()) return String(note).trim();

    const desc = String(description || '').trim();
    if (!desc) return lang.wallet_reason_other || DEFAULT_REASONS.wallet_reason_other;

    const key = REASON_MAP[desc];
    const mapped = translateReasonKey(key, lang);
    if (mapped) return mapped;

    const lower = desc.toLowerCase();
    if (lower.includes('wallet') || lower.includes('recharge') || lower.includes('recarga')) {
        return lang.wallet_reason_wallet_recharge || DEFAULT_REASONS.wallet_reason_wallet_recharge;
    }
    if (lower.includes('tip') || lower.includes('propina')) {
        return lang.wallet_reason_tip || DEFAULT_REASONS.wallet_reason_tip;
    }
    if (lower.includes('referral') || lower.includes('referido')) {
        return lang.wallet_reason_referral || DEFAULT_REASONS.wallet_reason_referral;
    }
    if (lower.includes('cancellation') || lower.includes('cancel') || lower.includes('penal')) {
        return lang.wallet_reason_cancellation_fee || DEFAULT_REASONS.wallet_reason_cancellation_fee;
    }
    if (lower.includes('publicidad') || lower.includes('advert') || lower.includes('adclick')) {
        return lang.wallet_reason_ad_recharge || DEFAULT_REASONS.wallet_reason_ad_recharge;
    }
    if (lower.includes('comision') || lower.includes('commission') || lower.includes('platform')) {
        return lang.wallet_reason_platform_fee_cash || DEFAULT_REASONS.wallet_reason_platform_fee_cash;
    }

    if (isOpaqueReference(desc)) {
        return lang.wallet_reason_operation || DEFAULT_REASONS.wallet_reason_operation;
    }

    return desc;
};

/**
 * Etiqueta legible para un movimiento de billetera (pantalla de historial).
 */
export const resolveWalletMovement = (item, language, context = {}) => {
    const lang = language || {};
    const txType = normalizeTxType(item?.type);
    const usertype = context.usertype || '';

    if (item?.note && String(item.note).trim()) {
        return resolveReason(String(item.note).trim(), lang, null);
    }

    const txRef = String(item?.txRef || '').trim();
    const txId = String(item?.transaction_id || '').trim();

    if (isKnownReasonCode(txRef)) {
        return translateReasonKey(REASON_MAP[txRef], lang);
    }

    if (txId.startsWith('penalty_')) {
        return lang.wallet_reason_penalty || DEFAULT_REASONS.wallet_reason_penalty;
    }

    if (txType === 'Withdraw') {
        return lang.wallet_reason_withdraw || DEFAULT_REASONS.wallet_reason_withdraw;
    }

    if (/^wallet-/i.test(txRef) || txRef === 'auto_recharge' || txRef === 'daviplata_wallet_recharge') {
        if (txRef === 'auto_recharge') {
            return lang.wallet_reason_auto_recharge || DEFAULT_REASONS.wallet_reason_auto_recharge;
        }
        return lang.wallet_reason_wallet_recharge || DEFAULT_REASONS.wallet_reason_wallet_recharge;
    }

    const refLooksLikeBooking = isOpaqueReference(txRef) || isOpaqueReference(txId);
    if (refLooksLikeBooking) {
        if (txType === 'Credit') {
            return lang.wallet_reason_trip_earning || DEFAULT_REASONS.wallet_reason_trip_earning;
        }
        if (txType === 'Debit') {
            if (usertype === 'driver') {
                return lang.wallet_reason_platform_fee_cash || DEFAULT_REASONS.wallet_reason_platform_fee_cash;
            }
            return lang.wallet_reason_trip_payment || DEFAULT_REASONS.wallet_reason_trip_payment;
        }
    }

    if (txRef) {
        const resolved = resolveReason(txRef, lang, null);
        const defaultOther = lang.wallet_reason_other || DEFAULT_REASONS.wallet_reason_other;
        if (resolved && resolved !== defaultOther) return resolved;
    }

    if (txType === 'Credit') {
        return lang.wallet_reason_wallet_recharge || DEFAULT_REASONS.wallet_reason_wallet_recharge;
    }
    if (txType === 'Debit') {
        return lang.wallet_reason_operation || DEFAULT_REASONS.wallet_reason_operation;
    }

    return lang.wallet_reason_other || DEFAULT_REASONS.wallet_reason_other;
};

/** Referencia corta opcional (reserva / transacción interna). */
export const formatWalletReference = (item) => {
    const ref = String(item?.txRef || item?.transaction_id || '').trim();
    if (!ref || !isOpaqueReference(ref)) return null;
    if (ref.length <= 16) return ref;
    return `${ref.slice(0, 8)}…${ref.slice(-4)}`;
};

export const buildWalletNotification = ({ type, amount, description, settings, language, note }) => {
    const lang = language || {};
    const formattedAmount = formatAmount(amount, settings);
    const reason = resolveReason(description, lang, note);

    if (type === 'credit') {
        return {
            title: lang.wallet_notify_credit_title || 'Abono a billetera',
            msg: applyTemplate(
                lang.wallet_notify_credit_msg || 'Se sumaron {amount} a tu billetera por: {reason}',
                { amount: formattedAmount, reason }
            ),
        };
    }

    return {
        title: lang.wallet_notify_debit_title || 'Cargo a billetera',
        msg: applyTemplate(
            lang.wallet_notify_debit_msg || 'Se descontaron {amount} de tu billetera por: {reason}',
            { amount: formattedAmount, reason }
        ),
    };
};

export default buildWalletNotification;
